import { Component, Input } from '@angular/core';

const EXAMPLE_RATES: Record<string, number> = {
  EUR: 1.125,
  GBP: 1.31,
  JPY: 0.0067,
  USD: 1,
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  USD: '$',
};

@Component({
  selector: 'app-vendor-margin-test',
  templateUrl: './vendor-margin-test.component.html',
  styleUrls: ['./vendor-margin-test.component.css'],
})
export class VendorMarginTestComponent {
  @Input() vendorName = '';

  currencies = ['EUR', 'GBP', 'JPY', 'USD'];
  currency = 'EUR';
  foreignCost = 120;
  exchangeRate = EXAMPLE_RATES.EUR;
  shipping = 10.93;
  duty = 8.11;
  multiple = 3;

  get symbol(): string {
    return CURRENCY_SYMBOLS[this.currency] || this.currency;
  }

  get convertedCost(): number {
    return this.num(this.foreignCost) * this.num(this.exchangeRate);
  }

  get convertedShipping(): number {
    return this.num(this.shipping) * this.num(this.exchangeRate);
  }

  get convertedDuty(): number {
    return this.num(this.duty) * this.num(this.exchangeRate);
  }

  get landed(): number {
    return this.convertedCost + this.convertedShipping + this.convertedDuty;
  }

  get proposedRetail(): number {
    return this.landed * this.num(this.multiple);
  }

  get grossProfit(): number {
    return this.proposedRetail - this.landed;
  }

  get marginPercent(): number {
    return this.proposedRetail ? (100 * this.grossProfit) / this.proposedRetail : 0;
  }

  get signal(): 'red' | 'yellow' | 'green' {
    const margin = Math.round(this.marginPercent * 10) / 10;
    if (margin < 60) {
      return 'red';
    }
    if (margin < 66.7) {
      return 'yellow';
    }
    return 'green';
  }

  get signalLabel(): string {
    if (this.signal === 'red') {
      return 'Below minimum margin floor';
    }
    if (this.signal === 'yellow') {
      return 'Above floor, below target';
    }
    return 'Target margin met';
  }

  onCurrencyChange(): void {
    this.exchangeRate = EXAMPLE_RATES[this.currency] ?? 1;
  }

  onRetailChange(value: string): void {
    const retail = this.num(value);
    this.multiple = this.landed ? retail / this.landed : 0;
  }

  private num(value: number | string): number {
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
